import SeoHead from "@/components/SeoHead";
import FeaturePageLayout from "@/components/FeaturePageLayout";
import TypewriterDemo from "@/components/TypewriterDemo";
import { Flame, Target, Users, TrendingUp, BarChart3, Code, RefreshCw, Thermometer, Snowflake, CheckCircle } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const SCENARIOS = [
  {
    text: "Recalculating engagement scores for 12,450 subscribers...",
    results: [
      { icon: <Flame size={14} className="text-red-500" />, title: "Hot tier: 2,890 subscribers (23%)", desc: "Opened 3+ emails in the last 14 days. High purchase intent — target with premium offers." },
      { icon: <Thermometer size={14} className="text-amber-500" />, title: "Warm tier: 4,120 subscribers (33%)", desc: "Opened 1-2 emails recently. Engaged but not yet committed — nurture with value content." },
      { icon: <Snowflake size={14} className="text-blue-500" />, title: "Cold tier: 3,890 subscribers (31%)", desc: "No opens in 30+ days. Risk of churn — consider re-engagement or sunset." },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Scores updated", desc: "Next recalculation in 2 hours. 1,550 subscribers changed tiers since last run." },
    ],
  },
  {
    text: "Analyzing subscriber activity for Q1 campaign targeting...",
    results: [
      { icon: <Flame size={14} className="text-red-500" />, title: "Top performer: sarah@techco.com", desc: "Engagement score: 94/100. Opened 12 of last 15 emails. Clicked 8 times." },
      { icon: <TrendingUp size={14} className="text-emerald-500" />, title: "Rising: +340 moved warm → hot", desc: "Your last 3 campaigns drove significant engagement improvement across the warm tier." },
      { icon: <Snowflake size={14} className="text-blue-500" />, title: "At risk: 892 trending cold", desc: "These subscribers haven't opened in 20+ days. Re-engage before they churn." },
      { icon: <CheckCircle size={14} className="text-emerald-500" />, title: "Segmentation ready", desc: "Tier-based segments automatically updated. Ready for targeted campaigns." },
    ],
  },
];

export default function EngagementScoringLandingPage() {
  return (
    <>
      <SeoHead
        title="Engagement Scoring — Know Who's Hot, Warm & Cold | MoreMail"
        description="Every subscriber gets a 0-100 engagement score updated every 2 hours. Segment by hot, warm, cold, and new to send the right message to the right people."
        canonical={`${SITE_URL}/engagement-scoring`}
        keywords="email engagement scoring, subscriber scoring, email engagement tiers, hot warm cold subscribers, email segmentation by engagement"
        ogTitle="Engagement Scoring — Know Your Subscribers | MoreMail"
        ogDescription="0-100 engagement scores updated every 2 hours. Segment by hot, warm, cold, and new for targeted campaigns."
        jsonLd={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Engagement Scoring", applicationCategory: "BusinessApplication", description: "Automatic subscriber engagement scoring with tier-based segmentation.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }}
      />
      <FeaturePageLayout
        badge="Intelligence" badgeIcon={<Flame size={13} className="text-coral" />}
        heroTitle={<>Know Who's <em className="text-coral not-italic">Hot</em>,<br />Who's Not</>}
        heroSubtitle="Every subscriber gets a 0-100 engagement score updated every 2 hours. Segment by hot, warm, cold, and new to send the right message to the right people."
        stats={[{ value: "0-100", label: "Score range" }, { value: "2hr", label: "Update frequency" }, { value: "4", label: "Engagement tiers" }]}
        demo={<TypewriterDemo scenarios={SCENARIOS} title="Engagement Scoring Engine" buttonLabel="Score" />}
        benefitsTitle="Precision Targeting for Every Campaign"
        benefits={[
          { icon: <Target size={18} className="text-coral" />, title: "Target Your Best Subscribers", desc: "Send premium offers and VIP content exclusively to hot subscribers who are most likely to convert." },
          { icon: <TrendingUp size={18} className="text-coral" />, title: "Re-Engage the Warm Tier", desc: "Warm subscribers are on the edge. Targeted campaigns can push them back to hot before they go cold." },
          { icon: <Users size={18} className="text-coral" />, title: "Clean Your List Intelligently", desc: "Cold subscribers hurt deliverability. Use tiers to decide who to sunset and who to try one last win-back on." },
          { icon: <RefreshCw size={18} className="text-coral" />, title: "Always Up to Date", desc: "Scores recalculate every 2 hours automatically. No manual imports, no stale data." },
          { icon: <BarChart3 size={18} className="text-coral" />, title: "Engagement Dashboard", desc: "See your tier breakdown at a glance — how many hot, warm, cold, and new across your entire list." },
          { icon: <Code size={18} className="text-coral" />, title: "API Access", desc: "Query engagement stats and tier breakdowns via API for custom dashboards and CRM sync." },
        ]}
        ctaHeadline="Stop Sending Blind"
        ctaDescription="Know exactly how engaged each subscriber is. Send the right message to the right people at the right time."
        currentPath="/engagement-scoring"
      >
        {/* Scoring formula breakdown */}
        <section className="py-12 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-display text-2xl font-bold text-ink text-center mb-3">
              How the Score Is <span className="text-coral">Calculated</span>
            </h2>
            <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">
              A transparent, weighted formula based on 30 days of activity. Maximum score: 100 points.
            </p>
            <div className="space-y-3">
              {[
                { signal: "Email Opens", points: "+2 per unique open", max: "20 pts (10 opens)", pct: 20 },
                { signal: "Link Clicks", points: "+5 per click", max: "30 pts (6 clicks)", pct: 30 },
                { signal: "Replies", points: "+8 per reply", max: "24 pts (3 replies)", pct: 24 },
                { signal: "Recency", points: "+16 if active in 7 days, +8 if 14 days", max: "16 pts", pct: 16 },
                { signal: "Frequency", points: "+10 if active 3+ weeks out of last 4", max: "10 pts", pct: 10 },
              ].map(item => (
                <div key={item.signal} className="flex items-center gap-4 bg-paper rounded-xl p-4 border border-sand">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-bold text-ink">{item.signal}</span>
                      <span className="text-xs text-coral font-semibold">{item.max}</span>
                    </div>
                    <p className="text-xs text-slate">{item.points}</p>
                  </div>
                  <div className="w-16 h-2 rounded-full bg-sand overflow-hidden">
                    <div className="h-full rounded-full bg-coral" style={{ width: `${item.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-paper rounded-xl p-5 border border-sand">
              <h3 className="font-display font-bold text-ink text-sm mb-2">Tier Thresholds</h3>
              <div className="grid grid-cols-4 gap-3 text-center text-xs">
                <div><span className="inline-block w-3 h-3 rounded-full bg-red-400 mb-1" /><p className="font-bold text-ink">Hot</p><p className="text-slate">Score ≥ 60</p></div>
                <div><span className="inline-block w-3 h-3 rounded-full bg-amber-400 mb-1" /><p className="font-bold text-ink">Warm</p><p className="text-slate">Score ≥ 25</p></div>
                <div><span className="inline-block w-3 h-3 rounded-full bg-blue-400 mb-1" /><p className="font-bold text-ink">Cold</p><p className="text-slate">Score &lt; 25</p></div>
                <div><span className="inline-block w-3 h-3 rounded-full bg-slate/30 mb-1" /><p className="font-bold text-ink">New</p><p className="text-slate">No emails sent</p></div>
              </div>
            </div>
          </div>
        </section>
      </FeaturePageLayout>
    </>
  );
}
